<template>
  <section
    ref="filtersTop"
    class="rev-ras-post-list-with-filters-module-component"
    :style="{ backgroundColor: data.background_color }">
    <div class="rr-container">
      <div class="section-header">
        <h2
          v-if="data.section_title"
          v-html="data.section_title"></h2>

        <div
          v-if="categoryOptions.length"
          class="desktop-filters"
          aria-label="Filter articles by category">
          <span>Filter by:</span>
          <button
            v-for="category in categoryOptions"
            :key="`article-filter-${category.value}`"
            type="button"
            :class="{ active: isCategorySelected(category.value) }"
            :aria-pressed="isCategorySelected(category.value)"
            @click="toggleCategory(category.value)">
            {{ category.label }}
          </button>
        </div>

        <div
          v-if="categoryOptions.length"
          ref="mobileFilter"
          class="mobile-filter">
          <span>Filter by:</span>
          <div
            class="mobile-filter-select"
            :class="{ active: isMobileFilterOpen }"
            data-lenis-prevent>
            <button
              class="mobile-filter-trigger"
              type="button"
              aria-haspopup="listbox"
              :aria-expanded="isMobileFilterOpen"
              aria-controls="revras-mobile-filter-options"
              @click="isMobileFilterOpen = !isMobileFilterOpen">
              <span>{{ mobileFilterLabel }}</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 16 10">
                <path d="m1 1 7 7 7-7" />
              </svg>
            </button>

            <div
              v-show="isMobileFilterOpen"
              id="revras-mobile-filter-options"
              class="mobile-filter-options"
              role="listbox"
              aria-multiselectable="true">
              <button
                v-for="category in categoryOptions"
                :key="`article-filter-option-${category.value}`"
                class="mobile-filter-option"
                type="button"
                role="option"
                :class="{ active: isCategorySelected(category.value) }"
                :aria-selected="isCategorySelected(category.value)"
                @click="toggleCategory(category.value)">
                <span
                  class="mobile-filter-checkbox"
                  aria-hidden="true">
                  <svg viewBox="0 0 12 9">
                    <path d="m1 4 3 3 7-6" />
                  </svg>
                </span>
                <span>{{ category.label }}</span>
              </button>

              <button
                v-if="selectedCategories.length"
                class="mobile-filter-clear"
                type="button"
                @click="clearCategories">
                Clear filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="article-list article-list-loading"
        aria-label="Loading articles"
        aria-busy="true">
        <article
          v-for="item in postsPerPage"
          :key="`revras-article-skeleton-${item}`"
          class="article-card article-card-skeleton">
          <span class="skeleton-image"></span>
          <span class="skeleton-content"> <i></i><i></i><i></i> </span>
        </article>
      </div>

      <p
        v-else-if="errorMessage"
        class="article-message"
        role="alert">
        {{ errorMessage }}
      </p>

      <p
        v-else-if="!filteredArticles.length"
        class="article-message">
        No articles were found for this category.
      </p>

      <div
        v-else
        ref="articleList"
        class="article-list"
        aria-live="polite">
        <article
          v-for="article in paginatedArticles"
          :key="article.id">
          <a
            class="article-card"
            :href="article.permalink"
            :aria-label="`Read ${plainText(article.title)}`">
            <div class="article-image">
              <img
                v-if="article.imageUrl"
                :src="article.imageUrl"
                :alt="article.imageAlt || plainText(article.title)"
                loading="lazy"
                @load="scheduleArticleHeightUpdate" />
              <span
                v-else
                class="article-image-placeholder"
                aria-hidden="true">
                RevRAS
              </span>
            </div>

            <div class="article-content">
              <div class="article-heading">
                <time
                  v-if="article.date"
                  class="mobile"
                  >{{ article.date }}</time
                >
                <p
                  v-if="article.categories.length"
                  class="article-category">
                  {{ article.categories[0].name }}
                </p>
                <span
                  class="article-rule"
                  aria-hidden="true"></span>
                <h3 v-html="article.title"></h3>
                <div
                  v-if="article.description"
                  class="article-description"
                  v-html="article.description"></div>
              </div>

              <div class="article-footer">
                <time
                  v-if="article.date"
                  class="desktop"
                  >{{ article.date }}</time
                >
                <span
                  class="read-more"
                  aria-hidden="true">
                  Read more
                </span>
              </div>
            </div>
          </a>
        </article>
      </div>

      <nav
        v-if="totalPages > 1"
        class="paginator-container"
        aria-label="Articles pagination">
        <div class="paginator-wrapper">
          <div class="paginator">
            <button
              class="page-select prev"
              type="button"
              aria-label="Previous page"
              :disabled="currentPage === 1"
              @click="goToPage(currentPage - 1)">
              <span class="arrow-container">
                <img
                  src="@/assets/img/caret-down-progressive.svg"
                  alt="" />
              </span>
            </button>

            <ul class="pages">
              <li
                v-for="page in totalPages"
                :key="`revras-article-page-${page}`">
                <button
                  type="button"
                  :class="{ active: currentPage === page }"
                  :aria-label="`Page ${page}`"
                  :aria-current="currentPage === page ? 'page' : null"
                  @click="goToPage(page)">
                  {{ page }}
                </button>
              </li>
            </ul>

            <button
              class="page-select next"
              type="button"
              aria-label="Next page"
              :disabled="currentPage === totalPages"
              @click="goToPage(currentPage + 1)">
              <span class="arrow-container">
                <img
                  src="@/assets/img/caret-down-progressive.svg"
                  alt="" />
              </span>
            </button>
          </div>
        </div>
      </nav>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "RevRASPostListWithFiltersComponent",
  "CLASSNAME": "rev-ras-post-list-with-filters-module-component",
  "URL": "http://vuevitewp.local/test"
 }
#ENDMETA */

import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useStore } from '@/stores/main'
import useSmoothScroll from '@/components/composables/useSmoothScroll'

const props = defineProps(['data'])
const store = useStore()
const { locoScroll } = useSmoothScroll()
const articles = ref([])
const selectedCategories = ref([])
const currentPage = ref(1)
const isLoading = ref(true)
const errorMessage = ref('')
const filtersTop = ref(null)
const mobileFilter = ref(null)
const articleList = ref(null)
const isMobileFilterOpen = ref(false)
let articleHeightFrame = null

const postsPerPage = computed(() => {
  const configured = Number.parseInt(props.data?.posts_per_page, 10)
  return Number.isFinite(configured) && configured > 0 ? configured : 4
})

const categoryOptions = computed(() => {
  const categories = new Map()

  articles.value.forEach((article) => {
    article.categories.forEach((category) => {
      if (!categories.has(category.slug))
        categories.set(category.slug, category)
    })
  })

  return Array.from(categories.values()).map((category) => ({
    value: category.slug,
    label: category.name,
  }))
})

const filteredArticles = computed(() => {
  if (!selectedCategories.value.length) return articles.value

  return articles.value.filter((article) =>
    article.categories.some((category) =>
      selectedCategories.value.includes(category.slug)
    )
  )
})

const selectedCategoryLabels = computed(() =>
  categoryOptions.value
    .filter((category) => selectedCategories.value.includes(category.value))
    .map((category) => category.label)
)

const mobileFilterLabel = computed(() => {
  if (!selectedCategoryLabels.value.length) return 'All Posts'
  if (selectedCategoryLabels.value.length === 1) {
    return selectedCategoryLabels.value[0]
  }

  return `${selectedCategoryLabels.value.length} categories selected`
})

const totalPages = computed(() =>
  Math.ceil(filteredArticles.value.length / postsPerPage.value)
)

const paginatedArticles = computed(() => {
  const start = (currentPage.value - 1) * postsPerPage.value
  return filteredArticles.value.slice(start, start + postsPerPage.value)
})

const updateArticleHeights = () => {
  if (!articleList.value) return

  const cards = Array.from(articleList.value.querySelectorAll('.article-card'))

  if (!cards.length) return

  cards.forEach((card) => card.style.removeProperty('height'))

  const tallestHeight = Math.max(
    ...cards.map((card) => Math.ceil(card.getBoundingClientRect().height))
  )

  cards.forEach((card) => {
    card.style.height = `${tallestHeight}px`
  })

  locoScroll.value?.resize?.()
}

const scheduleArticleHeightUpdate = () => {
  if (articleHeightFrame !== null) {
    window.cancelAnimationFrame(articleHeightFrame)
  }

  articleHeightFrame = window.requestAnimationFrame(() => {
    articleHeightFrame = null
    updateArticleHeights()
  })
}

const normalizeCategories = (article) => {
  const categorySource = Array.isArray(article?.article_filter_categories)
    ? article.article_filter_categories
    : article?.acf?.article_categories

  if (!Array.isArray(categorySource)) return []

  return categorySource
    .filter((category) => category && typeof category === 'object')
    .map((category) => ({
      id: category.id || category.term_id,
      name: category.name || '',
      slug: category.slug || String(category.id || category.term_id || ''),
    }))
    .filter((category) => category.name && category.slug)
}

const formatDate = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

const normalizeArticle = (article) => {
  const image = article?.acf?.carrousel_image

  return {
    id: article.id,
    title: article?.title?.rendered || article?.post_title || '',
    description: article?.acf?.description || '',
    permalink: article?.link || article?.permalink || '#',
    imageUrl: typeof image === 'string' ? image : image?.url || '',
    imageAlt: typeof image === 'object' ? image?.alt || '' : '',
    date: article?.acf?.published_date || formatDate(article?.date),
    categories: normalizeCategories(article),
  }
}

const requestArticles = async (params) => {
  const request = {
    type: 'articles',
    params,
    showLoading: false,
  }

  await store.getItems(request)

  return {
    items: store.requestedItems(request),
    totalPages: Number.parseInt(store.request(request)?.totalPages, 10) || 1,
  }
}

const loadArticles = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const baseParams = {
      per_page: 100,
      orderby: 'date',
      order: 'asc',
    }
    const firstPage = await requestArticles(baseParams)
    const loadedArticles = [...firstPage.items]

    for (let page = 2; page <= firstPage.totalPages; page += 1) {
      const nextPage = await requestArticles({ ...baseParams, page })
      loadedArticles.push(...nextPage.items)
    }

    articles.value = loadedArticles.map(normalizeArticle)
  } catch (error) {
    console.error('Unable to load RevRAS articles.', error)
    errorMessage.value =
      'Articles are temporarily unavailable. Please try again later.'
  } finally {
    isLoading.value = false
  }
}

const isCategorySelected = (category) =>
  selectedCategories.value.includes(category)

const toggleCategory = (category) => {
  selectedCategories.value = isCategorySelected(category)
    ? selectedCategories.value.filter((item) => item !== category)
    : [...selectedCategories.value, category]
  resetPagination()
}

const clearCategories = () => {
  selectedCategories.value = []
  resetPagination()
}

const resetPagination = () => {
  currentPage.value = 1
}

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return
  currentPage.value = page

  nextTick(() => {
    window.requestAnimationFrame(() => {
      if (!filtersTop.value) return

      const top = Math.max(
        0,
        window.scrollY + filtersTop.value.getBoundingClientRect().top - 110
      )

      if (locoScroll.value?.scrollTo) {
        locoScroll.value.scrollTo(top, { duration: 0.8, force: true })
        return
      }

      window.scrollTo({ top, behavior: 'smooth' })
    })
  })
}

const plainText = (value) => String(value || '').replace(/<[^>]*>/g, '')

const closeMobileFilter = (event) => {
  if (
    isMobileFilterOpen.value &&
    mobileFilter.value &&
    !mobileFilter.value.contains(event.target)
  ) {
    isMobileFilterOpen.value = false
  }
}

watch([totalPages, postsPerPage], ([pages]) => {
  if (pages && currentPage.value > pages) currentPage.value = pages
})

watch(
  paginatedArticles,
  async () => {
    await nextTick()
    scheduleArticleHeightUpdate()
  },
  { flush: 'post' }
)

onBeforeMount(loadArticles)
onMounted(() => {
  document.addEventListener('click', closeMobileFilter)
  window.addEventListener('resize', scheduleArticleHeightUpdate)
  scheduleArticleHeightUpdate()
})
onBeforeUnmount(() => {
  document.removeEventListener('click', closeMobileFilter)
  window.removeEventListener('resize', scheduleArticleHeightUpdate)

  if (articleHeightFrame !== null) {
    window.cancelAnimationFrame(articleHeightFrame)
  }
})
</script>

<style lang="scss" scoped>
.rev-ras-post-list-with-filters-module-component {
  @apply relative pt-[50px] md:pt-[60px] lg:pt-[70px] pb-[40px];

  &:before {
    @apply absolute top-0 left-0 w-full h-full z-[1] bg-balanced;
    content: '';
    @apply rounded-tr-[50px] md:rounded-tr-[60px] lg:rounded-tr-[80px];
  }

  .rr-container {
    .section-header {
      @apply mb-7 flex flex-col gap-5 lg:mb-7;

      h2 {
        @apply header-2 text-white;
      }

      .desktop-filters {
        @apply hidden items-center gap-3 lg:flex lg:flex-wrap;

        > span {
          @apply w-full text-approachable text-[22px];
        }

        button {
          @apply rounded-full border font-normal border-approachable bg-transparent px-4 py-[10px] text-[22px] leading-none text-approachable transition-colors;
          @apply font-FKGrotesk hover:bg-approachable hover:text-humble;

          &.active {
            @apply bg-approachable text-humble;
          }
        }
      }

      .mobile-filter {
        @apply relative z-3 flex flex-col gap-2 lg:hidden;

        > span:first-child {
          @apply text-[14px] text-white;
        }

        .mobile-filter-select {
          @apply relative block w-full max-w-[320px];

          .mobile-filter-trigger {
            @apply relative flex min-h-[35px] w-full items-center rounded-full border border-approachable bg-transparent px-4 py-2 pr-12 text-left text-[13px] leading-none text-approachable transition-colors hover:border-progressive;

            > span {
              @apply line-clamp-1 text-inherit;
            }

            > svg {
              @apply pointer-events-none absolute right-5 top-1/2 h-[10px] w-4 -translate-y-1/2 transition-transform duration-200;

              path {
                fill: none;
                stroke: #70ffe5;
                stroke-linecap: round;
                stroke-linejoin: round;
                stroke-width: 1.5;
              }
            }
          }

          .mobile-filter-options {
            @apply absolute left-0 top-[calc(100%_+_8px)] z-4 max-h-[300px] w-full overflow-y-auto rounded-[15px] bg-white px-2 py-3 shadow-[0px_15px_23px_0px_rgba(0,0,0,0.15)];

            .mobile-filter-option {
              @apply flex w-full items-center gap-3 rounded-[8px] px-3 py-2.5 text-left text-[15px] text-humble transition-colors hover:bg-functional;

              .mobile-filter-checkbox {
                @apply flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border border-balanced bg-white;

                svg {
                  @apply h-[9px] w-3 opacity-0;

                  path {
                    fill: none;
                    stroke: #0e4343;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    stroke-width: 1.5;
                  }
                }
              }

              &.active {
                @apply bg-functional;

                .mobile-filter-checkbox {
                  @apply border-progressive bg-progressive;

                  svg {
                    @apply opacity-100;
                  }
                }
              }
            }

            .mobile-filter-clear {
              @apply mx-3 mt-2 border-0 bg-transparent p-0 text-[14px] text-balanced underline underline-offset-2;
            }

            &::-webkit-scrollbar {
              @apply w-[10px];
            }

            &::-webkit-scrollbar-track {
              @apply bg-transparent;
            }

            &::-webkit-scrollbar-thumb {
              @apply rounded-lg border-[3px] border-solid border-transparent bg-[rgba(30,107,92,0.47)] bg-clip-content;
            }
          }

          &.active {
            .mobile-filter-trigger {
              > svg {
                @apply rotate-180;
              }
            }
          }
        }
      }
    }

    .article-list {
      @apply flex flex-col gap-[22px] lg:gap-[15px];

      .article-card {
        @apply flex min-w-0 flex-col overflow-hidden rounded-[17px] bg-white no-underline md:min-h-[320px] md:flex-row;

        &:hover .article-image img,
        &:focus-visible .article-image img {
          @apply scale-[1.03];
        }

        .article-image {
          @apply relative rounded-[17px] block h-[203px] sm:h-[350px] w-full shrink-0 overflow-hidden md:h-auto md:w-[340px] lg:w-[440px];

          img {
            @apply h-full absolute top-0 left-0 w-full object-cover transition-transform duration-300;
          }

          .article-image-placeholder {
            @apply flex h-full w-full items-end p-5 text-[24px] font-medium text-white/70;
          }
        }

        .article-content {
          @apply flex min-w-0 flex-1 flex-col justify-between gap-6 px-5 pt-4 pb-8 md:px-8 md:py-8 lg:px-10;

          .article-category {
            @apply mb-5 text-[14px] leading-tight text-balanced;
          }

          .article-rule {
            @apply relative mb-5 block h-[3px] w-[33px] bg-humble;
            &:after {
              @apply absolute top-0 left-[100%] block h-[3px] w-[33px] bg-progressive;
              content: '';
            }
          }

          h3 {
            @apply line-clamp-3 text-[22px] font-medium leading-[1.25] text-humble lg:text-[26px] lg:leading-[40px];
          }

          .article-description {
            :deep(*) {
              @apply mt-3 line-clamp-3 header-5 normal-case font-normal font-FKGrotesk leading-[1.45] text-humble;
            }

            :deep(i),
            :deep(em) {
              @apply mt-0 inline overflow-visible;
              -webkit-box-orient: initial;
              -webkit-line-clamp: unset;
            }
          }

          time {
            @apply text-[12px] leading-tight text-balanced;

            &.mobile {
              @apply mb-2 block lg:hidden;
            }

            &.desktop {
              @apply hidden lg:block;
            }
          }

          .article-footer {
            @apply flex items-center justify-between gap-5;

            .read-more {
              @apply shrink-0 rounded-full bg-progressive px-5 py-1.5 text-[14px] leading-none text-humble transition-colors hover:bg-humble hover:text-white;
            }
          }
        }
      }

      &.article-list-loading {
        .article-card-skeleton {
          @apply animate-pulse;

          .skeleton-image {
            @apply h-[230px] w-full bg-white/20 md:h-auto md:w-[40%];
          }

          .skeleton-content {
            @apply flex flex-1 flex-col gap-5 p-8;

            i {
              @apply block h-4 rounded bg-gray_green_trans;

              &:first-child {
                @apply w-1/4;
              }

              &:last-child {
                @apply w-2/3;
              }
            }
          }
        }
      }
    }

    .article-message {
      @apply rounded-[15px] bg-white px-6 py-12 text-center text-[18px] text-balanced;
    }

    .paginator-container {
      @apply mt-10 flex flex-col items-center;

      .paginator-wrapper {
        @apply relative min-w-[290px] max-w-full rounded-[18px] bg-white/[0.63] p-2;

        .paginator {
          @apply flex h-20 items-center justify-between gap-5 rounded-[15px] px-5 [background:linear-gradient(90deg,rgba(30,107,92,0.06)_0%,rgba(30,107,92,0.06)_100%),rgba(255,255,255,0.62)];

          .page-select {
            @apply transition-opacity disabled:pointer-events-none disabled:opacity-30;

            .arrow-container {
              @apply flex h-[35px] w-[35px] items-center justify-center rounded-full bg-white;

              img {
                @apply transition-all;
              }
            }

            &.prev img {
              @apply rotate-90;
            }

            &.next img {
              @apply -rotate-90;
            }
          }

          .pages {
            @apply flex justify-center gap-[2px];

            li button {
              @apply h-[30px] w-[30px] rounded-full border border-transparent text-[22px] text-humble transition-all;

              &.active {
                @apply border-humble;
              }
            }
          }
        }
      }
    }
  }
}
</style>
